import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { X, UserPlus, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];

// Reusable "invite users" dialog powered by the generic /api/share endpoints.
// Props: collection (e.g. "gout_projects"), id (record id), title, onClose.
export const ShareDialog = ({ collection, id, title = "", onClose }) => {
  const [members, setMembers] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, u] = await Promise.all([
        api.get(`/share/${collection}/${id}/members`),
        api.get(`/share/users`),
      ]);
      setMembers(m.data);
      setUsers(u.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  }, [collection, id]);

  useEffect(() => { load(); }, [load]);

  const memberIds = new Set(members.map((m) => m.id));
  const available = users.filter((u) => !memberIds.has(u.id));

  const invite = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/share/${collection}/${id}/members`, { user_id: selected });
      setMembers(data);
      setSelected("");
      toast.success("Usuario invitado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  const remove = async (mid) => {
    try {
      const { data } = await api.delete(`/share/${collection}/${id}/members/${mid}`);
      setMembers(data);
      toast.success("Acceso removido");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease }} onClick={(e) => e.stopPropagation()}
        data-testid="share-dialog" className="w-full max-w-md bg-cream rounded-xl p-8">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-medium">Invitar usuarios</h3>
          <button data-testid="share-close" onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        {title && <p className="text-sm text-navy/50 mb-6">{title}</p>}

        <div className="flex items-center gap-2 mb-6">
          <select data-testid="share-user-select" value={selected} onChange={(e) => setSelected(e.target.value)}
            className="flex-1 bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm">
            <option value="">Selecciona un usuario…</option>
            {available.map((u) => (
              <option key={u.id} value={u.id}>{u.name || u.email} · {u.role}</option>
            ))}
          </select>
          <button data-testid="share-invite-btn" onClick={invite} disabled={!selected || busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-gradient-violet text-white text-sm px-4 py-2.5 hover:opacity-90 transition-opacity disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Invitar
          </button>
        </div>

        <div className="overline text-navy/40 mb-3">Con acceso</div>
        {loading ? (
          <div className="text-navy/40 text-sm py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {members.map((m) => (
              <div key={m.id} data-testid={`share-member-${m.id}`}
                className="flex items-center justify-between bg-white border border-navy/10 rounded-lg px-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                  <div className="text-xs text-navy/40 truncate">{m.email} · {m.role}</div>
                </div>
                {m.is_owner ? (
                  <span className="text-[10px] text-violet inline-flex items-center gap-1"><Check className="w-3 h-3" /> Dueño</span>
                ) : (
                  <button data-testid={`share-remove-${m.id}`} onClick={() => remove(m.id)}
                    className="text-navy/30 hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default ShareDialog;
